import { tierMeta, val, conf } from "@/lib/api";
import { ConfidenceChip, ConfidenceLegend } from "./ConfidenceChip";
import {
  ShieldAlert,
  UserCog,
  ScrollText,
  CalendarClock,
  Flag,
  ArrowRight,
  Link2,
  Check,
  Info,
  Building2,
} from "lucide-react";

const SectionCard = ({ children, testid, className = "" }) => (
  <section
    data-testid={testid}
    className={`rounded-xl border border-line bg-white p-6 shadow-sm md:p-8 ${className}`}
  >
    {children}
  </section>
);

const IconBadge = ({ icon: Icon, dark }) => (
  <span
    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
      dark ? "bg-white/15" : "bg-aqua/15"
    }`}
  >
    <Icon className={`h-5 w-5 ${dark ? "text-aqua" : "text-asphalt"}`} />
  </span>
);

const FlagCard = ({ label, active, node, icon: Icon }) => (
  <div
    className="rounded-lg border p-4"
    data-testid={`flag-${label}`}
    style={
      active
        ? { background: "rgba(224,161,0,0.10)", borderColor: "rgba(224,161,0,0.45)" }
        : { background: "#FFFFFF", borderColor: "#E4E7EC" }
    }
  >
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-asphalt" />
      <span className="text-sm font-medium text-asphalt">{label}</span>
    </div>
    <div className="mt-2 flex items-center justify-between">
      <span
        className="rounded px-2 py-0.5 text-xs font-bold"
        style={
          active
            ? { background: "#E0A100", color: "#FFFFFF" }
            : { background: "#EEF1F4", color: "#667085" }
        }
      >
        {active ? "Flagged" : "Not flagged"}
      </span>
      <ConfidenceChip level={conf(node)} />
    </div>
  </div>
);

export const ResultView = ({ data, onCopyLink, copied, cacheHit, generatedAt }) => {
  if (!data) return null;
  const tier = val(data.risk_tier, "minimal");
  const meta = tierMeta(tier);
  const name = (data.meta && (data.meta.system_name || data.meta.input_summary)) || "Your AI system";
  const summary = data.summary || (data.meta && data.meta.input_summary) || val(data.risk_tier, "");

  const role = data.role || {};
  const rep = data.authorised_representative || {};
  const repRequired = val(rep.required, false);
  const prohibited = data.prohibited_practice || {};
  const gpai = data.gpai || {};
  const transparency = data.transparency_duties || {};
  const obligations = (data.obligations || []).filter((o) => o.applies !== false);
  const timeline = data.timeline || [];
  const nextSteps = data.next_steps || [];
  const annex = val(data.annex_iii_category, "");

  return (
    <div className="space-y-6">
      {/* Hero verdict */}
      <section
        data-testid="hero-verdict"
        className="rounded-xl border-2 p-6 md:p-8"
        style={{ borderColor: meta.color, background: meta.bg }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <span
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide"
              style={{ background: meta.color, color: tier === "minimal" ? "#364F6B" : "#FFFFFF" }}
              data-testid="risk-tier-badge"
            >
              <ShieldAlert className="h-3.5 w-3.5" /> {meta.label}
            </span>
            <h1 className="mt-4 text-2xl font-bold text-asphalt md:text-3xl">{name}</h1>
            <p className="mt-2 max-w-2xl text-base leading-relaxed text-asphalt">{summary}</p>
          </div>
          <button
            onClick={onCopyLink}
            data-testid="copy-link-btn"
            className="flex items-center gap-2 rounded-brand px-3.5 py-2 text-sm font-medium text-white transition-colors"
            style={{ background: "#F0508A" }}
          >
            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {copied ? "Copied" : "Copy results link"}
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-asphalt/10 pt-4">
          <span className="flex items-center gap-1.5 text-xs text-muted">
            <Info className="h-3.5 w-3.5" /> Indicative guidance, not legal advice.
          </span>
          {cacheHit && generatedAt && (
            <span className="text-xs text-muted" data-testid="cache-note">
              Result from cache, generated {new Date(generatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </section>

      {/* Role + Representative */}
      <div className="grid gap-6 lg:grid-cols-2">
        <section
          data-testid="role-card"
          className="rounded-xl border border-asphalt bg-asphalt p-6 text-white shadow-sm md:p-8"
        >
          <div className="flex items-center gap-3">
            <IconBadge icon={UserCog} dark />
            <h2 className="text-lg font-bold text-white">Your role under the Act</h2>
          </div>
          <p className="mt-4 text-3xl font-bold capitalize text-white">{val(role, "unknown")}</p>
          <div className="mt-2">
            <ConfidenceChip level={conf(role)} />
          </div>
          <p className="mt-3 text-sm leading-relaxed text-white/80">{role.rationale}</p>
        </section>

        <SectionCard
          testid="article-22-card"
          className={repRequired ? "border-2 border-aqua-dark" : ""}
        >
          <div className="flex items-center gap-3">
            <IconBadge icon={Building2} />
            <h2 className="text-lg font-bold text-asphalt">Article 22 EU representative</h2>
          </div>
          <p className="mt-4 text-3xl font-bold text-asphalt">
            {repRequired ? "Required" : "Not required"}
          </p>
          <div className="mt-2">
            <ConfidenceChip level={conf(rep.required)} />
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted">{val(rep.reason, "")}</p>
          {repRequired && (
            <a
              href="https://gdprlocal.com/ai-representative/"
              className="gl-primary-btn mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm"
              data-testid="article-22-cta"
            >
              GDPRLocal EU AI Act representative service <ArrowRight className="h-4 w-4" />
            </a>
          )}
        </SectionCard>
      </div>

      {/* Flags row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="flags-row">
        <FlagCard label="Prohibited practice" active={val(prohibited.flagged, false)} node={prohibited.flagged} icon={ShieldAlert} />
        <FlagCard label="General-purpose AI" active={val(gpai.is_gpai, false)} node={gpai.is_gpai} icon={Flag} />
        <FlagCard
          label="Transparency duties"
          active={!!val(transparency, "") && val(transparency, "").toLowerCase() !== "none"}
          node={transparency}
          icon={ScrollText}
        />
      </div>

      {annex && annex.toLowerCase() !== "none" && (
        <SectionCard testid="annex-card">
          <h2 className="text-base font-bold text-asphalt">Annex III high-risk area</h2>
          <p className="mt-2 text-sm text-asphalt">
            <span className="font-medium capitalize">{annex}</span>
          </p>
          {data.annex_iii_category && data.annex_iii_category.rationale && (
            <p className="mt-2 text-sm text-muted">{data.annex_iii_category.rationale}</p>
          )}
        </SectionCard>
      )}

      {/* Obligations */}
      {obligations.length > 0 && (
        <SectionCard testid="obligations-checklist">
          <div className="flex items-center gap-3">
            <IconBadge icon={ScrollText} />
            <h2 className="text-lg font-bold text-asphalt">Obligations that apply to you</h2>
          </div>
          <ul className="mt-4 space-y-3">
            {obligations.map((o, i) => (
              <li key={i} className="flex gap-3 rounded-lg border border-line bg-tint p-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-aqua/25">
                  <Check className="h-3.5 w-3.5 text-asphalt" />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-asphalt">{o.name}</span>
                    {o.confidence && <ConfidenceChip level={o.confidence} />}
                  </div>
                  {o.note && <p className="mt-1 text-xs text-muted">{o.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <section
          data-testid="timeline-strip"
          className="rounded-xl border border-asphalt bg-asphalt p-6 text-white shadow-sm md:p-8"
        >
          <div className="flex items-center gap-3">
            <IconBadge icon={CalendarClock} dark />
            <h2 className="text-lg font-bold text-white">When these obligations take effect (past/future)</h2>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {timeline.map((t, i) => (
              <div key={i} className="rounded-lg border border-white/15 bg-white/10 p-4">
                <p className="text-sm font-bold text-aqua">{t.applies_from}</p>
                <p className="mt-1 text-xs text-white/80">{t.obligation}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Next steps */}
      {nextSteps.length > 0 && (
        <SectionCard testid="next-steps">
          <h2 className="text-lg font-bold text-asphalt">How GDPRLocal can help</h2>
          <p className="mt-1 text-sm text-muted">Mapped to your findings. Neutral and optional.</p>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {nextSteps.map((s, i) => (
              <a
                key={i}
                href={s.url || "https://gdprlocal.com/"}
                className="group overflow-hidden rounded-lg border border-line bg-tint transition-colors hover:border-aqua-dark"
              >
                <div className="h-1.5 w-full bg-aqua" />
                <div className="p-4">
                  <p className="text-sm font-bold text-asphalt">{s.service}</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted">{s.why}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-asphalt">
                    Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="rounded-xl border border-line bg-white p-4 shadow-sm">
        <ConfidenceLegend />
      </div>
    </div>
  );
};
